export type SortOrder = "asc" | "desc";

export type SortAccessors<T> = Record<string, (record: T) => unknown>;

const LABEL_KEYS = [
  "full_name",
  "company_name",
  "team_name",
  "name",
  "stage_name",
  "title",
  "subject",
  "label",
];

const getObjectLabel = (value: unknown): unknown => {
  if (value === null || typeof value !== "object") return value;

  const record = value as Record<string, unknown>;
  const labelKey =
    LABEL_KEYS.find((key) => record[key] !== undefined && record[key] !== null) ||
    Object.keys(record).find(
      (key) => key.endsWith("display_name") && record[key] !== null
    );

  return labelKey ? record[labelKey] : null;
};

export const getSortValue = (record: any, field: string): unknown => {
  const value = record?.[field];

  if (Array.isArray(value)) {
    const labels = value.map(getObjectLabel).filter((item) => item !== null);
    return labels.length > 0 ? labels.join(", ") : null;
  }

  return getObjectLabel(value);
};

const toComparable = (value: unknown): number | string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  if (typeof value === "boolean") return value ? 1 : 0;

  const text = String(value).trim();
  if (text === "") return null;

  const numeric = Number(text);
  return Number.isFinite(numeric) ? numeric : text.toLowerCase();
};

export const sortRecords = <T>(
  records: T[],
  sortBy: string,
  sortOrder: SortOrder,
  accessors: SortAccessors<T> = {}
): T[] => {
  if (!sortBy || records.length === 0) return records;

  const getValue =
    accessors[sortBy] || ((record: T) => getSortValue(record, sortBy));

  return [...records].sort((a, b) => {
    const aValue = toComparable(getValue(a));
    const bValue = toComparable(getValue(b));

    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return 1;
    if (bValue === null) return -1;

    const comparison =
      typeof aValue === "number" && typeof bValue === "number"
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue));

    return sortOrder === "asc" ? comparison : -comparison;
  });
};
